/**
 * 지출예산편성조정 PoC
 * - 키: budgetExpenseAdjust|{tenant}|{fiscalYear}|{budgetKindCode}|{adjNo}
 * - 최초 로드 시 직전 단계(요구 또는 이전 조정) 데이터를 복사·시드
 * - 디테일: 전차수(prev*) 읽기전용 + 금회(calc*) 편집
 */

import {
  createDetailId,
  createRequestUid,
  enrichMasterRow,
  expenseReqStorageKey,
  filterExpenseRequests,
  loadExpenseRequests,
  validateExpenseRequests,
  defaultDetailRow,
  defaultMasterRow,
  TREE_MODE_OPTIONS,
  CALC_LEVEL_OPTIONS,
} from 'budget/budgetExpenseRequestStore';
import {
  findPreviousAdjStage,
  findRequestAdjStage,
  getBudgetRoundByKindCode,
  syncExpenseAdjustToDeptStatus,
} from 'budget/budgetRoundStore';
import { acctClassLabel, matchesAcctClass } from 'budget/budgetAcctStore';
import { rollupCalcDetails, sumTopLevelBudgetAmt } from 'utils/calcFormula';
import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList, cacheGet, cacheSet } from 'services/commonQuery';
import { createFundUid } from 'budget/budgetFundStore';

export const EXPENSE_ADJ_PREFIX = 'budgetExpenseAdjust';

const ADJ_LINE_SAVE_QUERY = [
  'BdExpFormation.insertExpAdjLine',
  'BdExpFormation.updateExpAdjLine',
  'BdExpFormation.deleteExpAdjLine',
];
const ADJ_FUND_SAVE_QUERY = [
  'BdExpFormation.insertExpAdjFund',
  'BdExpFormation.updateExpAdjFund',
  'BdExpFormation.deleteExpAdjFund',
];
const ADJ_CALC_SAVE_QUERY = [
  'BdExpFormation.insertExpAdjCalc',
  'BdExpFormation.updateExpAdjCalc',
  'BdExpFormation.deleteExpAdjCalc',
];

const adjustCache = new Map();

function adjustCacheKey(dims) {
  return [expenseAdjStorageKey(dims), String(dims?.acctClass || '').trim()].join('#');
}

export { TREE_MODE_OPTIONS, CALC_LEVEL_OPTIONS, filterExpenseRequests as filterExpenseAdjusts };
export { validateExpenseRequests as validateExpenseAdjusts };
export { defaultDetailRow, defaultMasterRow, createRequestUid, createDetailId };

export function expenseAdjStorageKey({ tenantCode, fiscalYear, budgetKindCode, adjNo }) {
  return [
    EXPENSE_ADJ_PREFIX,
    tenantCode || '',
    fiscalYear || '',
    String(budgetKindCode || '').trim(),
    String(adjNo ?? '').trim(),
  ].join('|');
}

function readPayload(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return Array.isArray(data?.rows) ? data.rows : null;
  } catch {
    return null;
  }
}

function writePayload(key, dims, rows) {
  localStorage.setItem(
    key,
    JSON.stringify({
      dims: {
        tenantCode: dims.tenantCode,
        fiscalYear: dims.fiscalYear,
        budgetKindCode: dims.budgetKindCode || '',
        adjNo: dims.adjNo,
      },
      rows: rows || [],
      savedAt: new Date().toISOString(),
    })
  );
}

/** 직전 단계 저장본 로드 (요구 또는 이전 조정) */
export async function loadPreviousStageRows(dims) {
  const round = getBudgetRoundByKindCode(dims.tenantCode, dims.fiscalYear, dims.budgetKindCode);
  if (!round) return { prevStage: null, rows: [] };
  const prevStage = findPreviousAdjStage(round.adjStages, dims.adjNo);
  if (!prevStage) return { prevStage: null, rows: [] };

  const reqStage = findRequestAdjStage(round.adjStages);
  const isReq =
    reqStage && Number(prevStage.adjNo) === Number(reqStage.adjNo);

  if (isReq) {
    // 요구 단계는 회계구분 필터 없이 전체 시드 후 화면에서 필터
    return {
      prevStage,
      rows: await loadExpenseRequests({
        tenantCode: dims.tenantCode,
        fiscalYear: dims.fiscalYear,
        budgetKindCode: dims.budgetKindCode,
        acctClass: '',
      }),
    };
  }

  const prevDims = { ...dims, adjNo: prevStage.adjNo, acctClass: '' };
  if (isBdApiEnabled()) {
    const rows = await loadAdjustLinesFromApi(prevDims);
    return { prevStage, rows };
  }
  const rows = readPayload(expenseAdjStorageKey(prevDims)) || [];
  return {
    prevStage,
    rows: rows.map((r) => enrichAdjustMasterRow({ ...r, mod: r.mod === 'D' ? 'D' : '' })),
  };
}

/** 전차수 금액을 prev* 로 고정하고 금회는 동일 값으로 시작 */
export function cloneMasterFromPrevious(master) {
  const srcDetails = Array.isArray(master?.details) ? master.details : [];
  const details = srcDetails
    .filter((d) => d && d.mod !== 'D')
    .map((d) => {
      const prevAmt = Number(d.calcAmt) || 0;
      const prevBasis = d.calcBasis || '';
      return {
        ...defaultDetailRow({
          ...d,
          mod: '',
          calcAmt: prevAmt,
          calcBasis: prevBasis,
        }),
        prevCalcBasis: prevBasis,
        prevCalcAmt: prevAmt,
        // 원본 detailId 유지 → 대사 라인 추적
        detailId: d.detailId || d.id || createDetailId(),
        id: d.detailId || d.id || createDetailId(),
      };
    });
  const enriched = enrichMasterRow({
    ...master,
    details,
    mod: '',
  });
  const prevBudgetAmt = Number(master.budgetAmt) || Number(enriched.budgetAmt) || 0;
  return enrichAdjustMasterRow({
    ...enriched,
    prevBudgetAmt,
    adjDiffAmt: (Number(enriched.budgetAmt) || 0) - prevBudgetAmt,
  });
}

export function enrichAdjustMasterRow(row) {
  const base = enrichMasterRow(row);
  const details = (base.details || []).map((d) => {
    // 신규 행은 전차수 0 · 시드/저장분은 prev* 유지
    const hasPrev = d.prevCalcAmt != null && d.prevCalcAmt !== '';
    const prevCalcAmt = hasPrev ? Number(d.prevCalcAmt) || 0 : 0;
    const prevCalcBasis = d.prevCalcBasis != null ? d.prevCalcBasis : hasPrev ? d.calcBasis || '' : '';
    const calcAmt = Number(d.calcAmt) || 0;
    return {
      ...d,
      prevCalcBasis,
      prevCalcAmt,
      adjDiffAmt: calcAmt - prevCalcAmt,
    };
  });
  const rolled = rollupCalcDetails(details, { inoutDiv: '지출' });
  const budgetAmt = sumTopLevelBudgetAmt(rolled, { inoutDiv: '지출' });
  const prevBudgetAmt =
    row.prevBudgetAmt != null && row.prevBudgetAmt !== ''
      ? Number(row.prevBudgetAmt) || 0
      : 0;
  const next = {
    ...base,
    details: rolled,
    budgetAmt,
    prevBudgetAmt,
    adjDiffAmt: budgetAmt - prevBudgetAmt,
  };
  if (next.confirmedAmt != null && next.confirmedAmt !== '') {
    next.finalAmt = (Number(next.confirmedAmt) || 0) + budgetAmt;
  }
  return next;
}

/** API 조정 라인 + 재원·산출 조립 (adjustUid = 라인 PK) */
async function loadAdjustLinesFromApi(dims) {
  const lines = await commonSelect('BdExpFormation.selectExpAdjLineList', {
    tenantCode: dims?.tenantCode,
    fiscalYear: dims?.fiscalYear,
    budgetKindCode: String(dims?.budgetKindCode || '').trim(),
    adjNo: Number(dims?.adjNo),
    acctClassCode: String(dims?.acctClass || '').trim() || null,
  });
  const rows = [];
  for (const line of lines) {
    const [funds, calcs] = await Promise.all([
      commonSelect('BdExpFormation.selectExpAdjFundList', { adjustUid: line.adjustUid }),
      commonSelect('BdExpFormation.selectExpAdjCalcList', { adjustUid: line.adjustUid }),
    ]);
    const single = !line.fundPkgUid && funds.length === 1 ? funds[0] : null;
    rows.push(
      enrichAdjustMasterRow({
        ...line,
        // FE 는 requestUid 키로 마스터를 다루므로 adjustUid 를 함께 노출
        requestUid: line.adjustUid,
        id: line.adjustUid,
        fundUid: single?.fundUid || '',
        fundCode: single?.fundCode || '',
        fundName: single?.fundName || '',
        fundAmounts: funds.map((f) => ({
          fundUid: f.fundUid || '',
          fundCode: f.fundCode || '',
          fundName: f.fundName || '',
          amount: Number(f.amount) || 0,
          sortOrder: Number(f.sortOrder) || 10,
        })),
        details: calcs.map((c) => ({
          detailId: c.detailId,
          id: c.detailId,
          calcLevel: Number(c.calcLevel) || 1,
          calcDesc: c.calcDesc || '',
          calcBasis: c.calcBasis || '',
          calcAmt: Number(c.calcAmt) || 0,
          remark: c.remark || '',
          basisLocked: c.basisLocked === 'Y' || c.basisLocked === true,
          mod: '',
        })),
        mod: '',
      })
    );
  }
  return rows;
}

/** 동기 조회용 — load 가 채운 캐시(API) 또는 localStorage(로컬). 시드는 하지 않음 */
export function getCachedExpenseAdjusts(dims) {
  const key = adjustCacheKey(dims);
  const cached = cacheGet(adjustCache, key);
  if (cached) return cached;
  if (isBdApiEnabled()) return [];
  const rows = readPayload(expenseAdjStorageKey(dims));
  if (!rows || !rows.length) return [];
  const acctClass = String(dims?.acctClass || '').trim();
  const list = acctClass
    ? rows.filter((r) => matchesAcctClass(r, acctClass, dims?.tenantCode))
    : rows;
  const enriched = list.map((r) => enrichAdjustMasterRow({ ...r, mod: r.mod === 'D' ? 'D' : '' }));
  cacheSet(adjustCache, key, enriched);
  return enriched;
}

/**
 * 조정 데이터 로드. 없으면 직전 단계에서 시드(저장은 사용자가 F10 할 때).
 */
export async function loadExpenseAdjusts(dims) {
  const kind = String(dims?.budgetKindCode || '').trim();
  const adjNo = dims?.adjNo;
  if (!kind || adjNo == null || adjNo === '') return [];
  const acctClass = String(dims?.acctClass || '').trim();

  let stored = null;
  if (isBdApiEnabled()) {
    const apiRows = await loadAdjustLinesFromApi(dims);
    stored = apiRows.length ? apiRows : null;
  } else {
    const rows = readPayload(expenseAdjStorageKey(dims));
    if (rows && rows.length) {
      const list = acctClass
        ? rows.filter((r) => matchesAcctClass(r, acctClass, dims?.tenantCode))
        : rows;
      stored = list.map((r) => enrichAdjustMasterRow({ ...r, mod: r.mod === 'D' ? 'D' : '' }));
    }
  }
  if (stored) {
    cacheSet(adjustCache, adjustCacheKey(dims), stored);
    return stored;
  }

  // 최초: 직전 단계 복사 (DB 시드 없음 — 저장 시점에 기록)
  const { rows: prevRows } = await loadPreviousStageRows(dims);
  const seeded = (prevRows || [])
    .filter((r) => r && r.mod !== 'D')
    .map((r) => cloneMasterFromPrevious(r));
  const list = acctClass
    ? seeded.filter((r) => matchesAcctClass(r, acctClass, dims?.tenantCode))
    : seeded;
  cacheSet(adjustCache, adjustCacheKey(dims), list);
  return list;
}

/** 조정 라인 자식(재원·산출) 전량 교체 */
async function replaceExpAdjChildren(row, addValue) {
  const adjustUid = row.adjustUid || row.requestUid;
  const [funds, calcs] = await Promise.all([
    commonSelect('BdExpFormation.selectExpAdjFundList', { adjustUid }),
    commonSelect('BdExpFormation.selectExpAdjCalcList', { adjustUid }),
  ]);
  await commonSaveList(
    ADJ_FUND_SAVE_QUERY,
    funds.map((f) => ({ adjFundUid: f.adjFundUid, mod: 'D' })),
    addValue
  );
  const fundSource =
    row.fundAmounts?.length || !(row.fundUid || row.fundCode)
      ? row.fundAmounts || []
      : [{ fundUid: row.fundUid, fundCode: row.fundCode, fundName: row.fundName, amount: row.budgetAmt }];
  await commonSaveList(
    ADJ_FUND_SAVE_QUERY,
    fundSource.map((f, idx) => ({
      adjFundUid: createFundUid('AF'),
      adjustUid,
      fundUid: f.fundUid || null,
      fundCode: f.fundCode || null,
      fundName: f.fundName || null,
      amount: Number(f.amount) || 0,
      sortOrder: (idx + 1) * 10,
      mod: 'A',
    })),
    addValue
  );
  await commonSaveList(
    ADJ_CALC_SAVE_QUERY,
    calcs.map((c) => ({ detailId: c.detailId, mod: 'D' })),
    addValue
  );
  await commonSaveList(
    ADJ_CALC_SAVE_QUERY,
    (row.details || []).map((d, idx) => ({
      detailId: d.detailId || createDetailId(),
      adjustUid,
      parentDetailId: d.parentDetailId || null,
      calcLevel: Number(d.calcLevel) || 1,
      calcDesc: d.calcDesc || null,
      calcBasis: d.calcBasis || null,
      calcAmt: Number(d.calcAmt) || 0,
      basisLocked: d.basisLocked ? 'Y' : 'N',
      sortOrder: (idx + 1) * 10,
      remark: d.remark || null,
      mod: 'A',
    })),
    addValue
  );
}

function expAdjLinePersistPayload(row, dims, kind, adjNo, exists) {
  return {
    adjustUid: row.adjustUid || row.requestUid,
    fiscalYear: dims?.fiscalYear,
    budgetKindCode: kind,
    adjNo: Number(adjNo),
    sourceRequestUid: row.sourceRequestUid || null,
    acctClassUid: row.acctClassUid || null,
    acctClassCode: row.acctClassCode || null,
    acctClassNm: row.acctClassNm || null,
    acctUnitUid: row.acctUnitUid || null,
    acctUnitCode: row.acctUnitCode || null,
    acctUnitNm: row.acctUnitNm || null,
    deptCode: row.deptCode || null,
    deptName: row.deptName || null,
    projectUid: row.projectUid || null,
    projectCode: row.projectCode || null,
    projectName: row.projectName || null,
    subjectUid: row.subjectUid || null,
    subjectCode: row.subjectCode || null,
    subjectDispCode: row.subjectDispCode || null,
    subjectName: row.subjectName || null,
    fundPkgUid: row.fundPkgUid || null,
    budgetAmt: Number(row.budgetAmt) || 0,
    remark: row.remark || null,
    mod: exists ? 'U' : 'A',
  };
}

export async function saveExpenseAdjusts(dims, rows) {
  const kind = String(dims?.budgetKindCode || '').trim();
  const adjNo = dims?.adjNo;
  if (!kind) throw new Error('예산차수(결합코드)가 없어 저장할 수 없습니다.');
  if (adjNo == null || adjNo === '') throw new Error('조정차수가 없어 저장할 수 없습니다.');

  const acctClass = String(dims?.acctClass || '').trim();
  const normalized = (rows || [])
    .filter((r) => r.mod !== 'D')
    .map((r) => {
      const enriched = enrichAdjustMasterRow(r);
      const rowAcctClass = acctClass || String(enriched.acctClassCode || '').trim();
      const {
        prevYearAmt: _py,
        confirmedAmt: _ca,
        finalAmt: _fa,
        adjDiffAmt: _ad,
        ...persistable
      } = enriched;
      return {
        ...persistable,
        mod: '',
        budgetKindCode: kind,
        adjNo: Number(adjNo),
        acctClassCode: rowAcctClass,
        acctClassNm: rowAcctClass ? acctClassLabel(rowAcctClass, dims?.tenantCode) : '',
        prevBudgetAmt: Number(enriched.prevBudgetAmt) || 0,
        details: (enriched.details || [])
          .filter((d) => d.mod !== 'D')
          .map((d) => ({
            detailId: d.detailId || createDetailId(),
            id: d.detailId || d.id || createDetailId(),
            calcLevel: Math.min(5, Math.max(1, Number(d.calcLevel) || 1)),
            calcDesc: d.calcDesc || '',
            calcBasis: d.calcBasis || '',
            calcAmt: Number(d.calcAmt) || 0,
            prevCalcBasis: d.prevCalcBasis != null ? d.prevCalcBasis : '',
            prevCalcAmt: d.prevCalcAmt != null ? Number(d.prevCalcAmt) || 0 : 0,
            remark: d.remark || '',
            basisLocked: !!d.basisLocked,
            mod: '',
          })),
        subjectUid: enriched.subjectUid || '',
        subjectDispCode: enriched.subjectDispCode || enriched.subjectCode || '',
        fundPkgUid: enriched.fundPkgUid || '',
        fundAmounts: Array.isArray(enriched.fundAmounts) ? enriched.fundAmounts : [],
      };
    });

  const key = expenseAdjStorageKey(dims);

  if (isBdApiEnabled()) {
    const addValue = { tenantCode: dims?.tenantCode, fiscalYear: dims?.fiscalYear };
    const dbLines = await commonSelect('BdExpFormation.selectExpAdjLineList', {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
      budgetKindCode: kind,
      adjNo: Number(adjNo),
    });
    const dbUids = new Set(dbLines.map((r) => r.adjustUid));

    const deleted = (rows || [])
      .filter((r) => r.mod === 'D')
      .map((r) => r.adjustUid || r.requestUid)
      .filter((uid) => uid && dbUids.has(uid))
      .map((uid) => ({ adjustUid: uid, mod: 'D' }));
    if (deleted.length) await commonSaveList(ADJ_LINE_SAVE_QUERY, deleted, addValue);

    const linePayload = normalized.map((r) =>
      expAdjLinePersistPayload(r, dims, kind, adjNo, dbUids.has(r.adjustUid || r.requestUid))
    );
    if (linePayload.length) await commonSaveList(ADJ_LINE_SAVE_QUERY, linePayload, addValue);
    for (const r of normalized) {
      await replaceExpAdjChildren(r, addValue);
    }

    const savedApi = await loadExpenseAdjusts(dims);
    const allApi = acctClass ? await loadAdjustLinesFromApi({ ...dims, acctClass: '' }) : savedApi;
    const deptSyncApi = await syncExpenseAdjustToDeptStatus(
      dims.tenantCode,
      dims.fiscalYear,
      kind,
      adjNo,
      allApi,
      { inoutDiv: '지출' }
    );
    return { rows: savedApi, deptSync: deptSyncApi };
  }

  const existing = readPayload(key) || [];
  const others = acctClass
    ? existing.filter((r) => !matchesAcctClass(r, acctClass, dims?.tenantCode))
    : [];
  const combined = [...others, ...normalized];
  writePayload(key, dims, combined);

  const saved = normalized.map((r) => enrichAdjustMasterRow({ ...r, mod: '' }));
  cacheSet(adjustCache, adjustCacheKey(dims), saved);
  const deptSync = await syncExpenseAdjustToDeptStatus(
    dims.tenantCode,
    dims.fiscalYear,
    kind,
    adjNo,
    combined.map((r) => enrichAdjustMasterRow({ ...r, mod: '' })),
    { inoutDiv: '지출' }
  );
  return { rows: saved, deptSync };
}

/** 디버그·이관용 — 요구 키와 혼동 방지 */
export function expenseReqKeyForCompare(dims) {
  return expenseReqStorageKey(dims);
}
